import { useEffect, useRef, useState } from 'react'
import AddProductModal from './AddProductModal'
import EditProductModal from './EditProductModal'
import ProductImageLightbox from './ProductImageLightbox'
import ProductItemCard from './ProductItemCard'
import { ShieldGlyph } from './AdminIcons'
import { useTranslation } from '../hooks/useTranslation'
import { adminDisplayName, articlesCountLabel } from '../utils/adminPolicy'
import { blurNumberOnWheel } from '../utils/numberInputWheel'
import '../pages/CartDetailPage.css'

const ADMIN_ITEM_ACTIONS = ['open', 'edit', 'delete']

export default function AdminCartWorkspace({
  detail,
  saveError,
  saveNotice = '',
  busy = false,
  addPending = false,
  editPending = false,
  addError = '',
  editError = '',
  onSaveCart,
  onSaveItem,
  onAddItem,
  onAskDelete,
  onBack,
}) {
  const { t } = useTranslation()
  const cart = detail?.cart || {}
  const owner = detail?.owner || {}
  const items = detail?.items || []
  const addButtonRef = useRef(null)
  const editTriggerRef = useRef(null)
  const [name, setName] = useState(cart.name || '')
  const [budget, setBudget] = useState(cart.budget ?? '')
  const [viewMode, setViewMode] = useState('list')
  const [previewUrl, setPreviewUrl] = useState('')
  const [editingItem, setEditingItem] = useState(null)
  const [showAdd, setShowAdd] = useState(false)
  const wasEditing = useRef(false)
  const wasAdding = useRef(false)

  useEffect(() => {
    if (wasEditing.current && !editPending && !editError) setEditingItem(null)
    wasEditing.current = editPending
  }, [editPending, editError])

  useEffect(() => {
    if (wasAdding.current && !addPending && !addError) setShowAdd(false)
    wasAdding.current = addPending
  }, [addPending, addError])

  return (
    <div className="admin-workspace admin-cart-workspace cart-detail-page">
      <div className="admin-workspace-nav">
        <button type="button" className="admin-back" onClick={onBack}>
          <img src="/Icon arrow back ios new.png" alt="" />
          Retour à la fiche
        </button>
        <p className="admin-cart-owner">
          <ShieldGlyph />
          <span>
            Administration · Propriétaire {adminDisplayName(owner)}
            {owner.email ? ` · ${owner.email}` : ''}
          </span>
        </p>
      </div>

      <div className="cart-detail-header">
        <h1 className="cart-name" id="admin-cart-title">{name || cart.name || 'Panier sans nom'}</h1>
        <div className="header-actions">
          <button
            ref={addButtonRef}
            type="button"
            className="btn-icon"
            onClick={() => setShowAdd(true)}
            title="Ajouter un produit"
          >
            <img src="/Icon add circle outline.png" alt="Ajouter" />
          </button>
        </div>
      </div>

      <form
        className="admin-cart-meta"
        onSubmit={(event) => {
          event.preventDefault()
          onSaveCart?.({ name, budget: budget === '' ? 0 : Number(budget) })
        }}
      >
        <label>
          Nom du panier
          <input value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <label>
          Budget
          <input type="number" inputMode="decimal" min="0" step="0.01" value={budget} onChange={(event) => setBudget(event.target.value)} onWheel={blurNumberOnWheel} />
        </label>
        <button type="submit" disabled={busy}>Enregistrer le panier</button>
      </form>

      {saveNotice ? <p className="admin-notice" role="status">{saveNotice}</p> : null}
      {saveError ? <p className="admin-state" role="alert">{saveError}</p> : null}

      <div className="cart-filters">
        <p className="admin-cart-count">{articlesCountLabel(items.length)}</p>
        <div className="view-mode-toggle">
          <button
            type="button"
            className="btn-icon-small"
            onClick={() => setViewMode((current) => (current === 'list' ? 'grid' : 'list'))}
            title={viewMode === 'list' ? 'Vue grille' : 'Vue liste'}
          >
            <img
              src={viewMode === 'list' ? '/Icon grid view.png' : '/Icon view list.png'}
              alt={viewMode === 'list' ? 'Vue grille' : 'Vue liste'}
            />
          </button>
        </div>
      </div>

      <div className="cart-detail-container">
        {items.length ? (
          <div className={viewMode === 'grid' ? 'items-grid' : 'items-list'}>
            {items.map((item) => (
              <ProductItemCard
                key={item.id}
                item={item}
                viewMode={viewMode}
                emptyLinkLabel={t('linkUrl')}
                includeActions={ADMIN_ITEM_ACTIONS}
                onEdit={(next) => {
                  editTriggerRef.current = document.activeElement
                  setEditingItem(next)
                }}
                onDelete={onAskDelete}
                onImageClick={setPreviewUrl}
              />
            ))}
          </div>
        ) : (
          <p className="admin-state">Aucun produit dans ce panier.</p>
        )}
      </div>

      <EditProductModal
        isOpen={Boolean(editingItem)}
        item={editingItem}
        isSubmitting={editPending}
        submitError={editError}
        returnFocusRef={editTriggerRef}
        t={t}
        onClose={() => { if (!editPending) setEditingItem(null) }}
        onSubmit={(data) => {
          if (!editingItem?.id) return
          onSaveItem?.(editingItem.id, data)
        }}
      />

      <AddProductModal
        isOpen={showAdd}
        isSubmitting={addPending}
        submitError={addError}
        returnFocusRef={addButtonRef}
        t={t}
        onClose={() => { if (!addPending) setShowAdd(false) }}
        onExtract={async () => {
          throw new Error('Extraction indisponible depuis l’administration')
        }}
        onSubmit={(data) => onAddItem?.(data)}
      />

      <ProductImageLightbox src={previewUrl} onClose={() => setPreviewUrl('')} />
    </div>
  )
}
